import React, { useEffect, useMemo, useRef, useState } from "react";
import { logWarn, logError } from "../lib/logger";
import { Country, State } from "country-state-city";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Save, Plus, Trash2, Linkedin, FileDown, AlertTriangle, Upload, Loader2, Sparkles, Check, X } from "lucide-react";
import { buildResumeHtml } from "../lib/resumePdf";

const uid = () => Math.random().toString(36).slice(2, 10);
const withUid = (item) => ({ ...item, _uid: uid() });
const stripUid = ({ _uid, ...rest }) => rest;

const emptyExp = { company: "", title: "", location: "", start_date: "", end_date: "", description: "" };
const emptyEdu = { school: "", degree: "", location: "", start_date: "", end_date: "" };

const emptyResume = {
  full_name: "", email: "", phone: "", location: "", country: "", state: "", headline: "",
  website: "", linkedin: "", github: "", portfolio: "",
  summary: "", skills: "",
  experience: [],
  education: [],
  projects: "",
};

// Word-level LCS diff. Splits on whitespace (keeping the separators) so we can
// highlight only the words that changed between the original and the polished text.
function wordDiff(before, after) {
  const a = (before || "").split(/(\s+)/);
  const b = (after || "").split(/(\s+)/);
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const beforeTokens = [], afterTokens = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { beforeTokens.push({ t: a[i], c: false }); afterTokens.push({ t: b[j], c: false }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { beforeTokens.push({ t: a[i], c: true }); i++; }
    else { afterTokens.push({ t: b[j], c: true }); j++; }
  }
  while (i < m) { beforeTokens.push({ t: a[i], c: true }); i++; }
  while (j < n) { afterTokens.push({ t: b[j], c: true }); j++; }
  return { beforeTokens, afterTokens };
}

const makeDiff = (key, label, original, polished) => ({
  key, label, original: original || "", polished, accepted: true, ...wordDiff(original || "", polished),
});

export default function Resume() {
  const [form, setForm] = useState(emptyResume);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [polishOpen, setPolishOpen] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [diffs, setDiffs] = useState([]); // [{key,label,original,polished,accepted}]
  const fileRef = useRef(null);

  const countries = useMemo(() => Country.getAllCountries(), []);
  const selectedCountryIso = useMemo(
    () => countries.find((c) => c.name === form.country)?.isoCode || "",
    [countries, form.country]
  );
  const states = useMemo(
    () => (selectedCountryIso ? State.getStatesOfCountry(selectedCountryIso) : []),
    [selectedCountryIso]
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/resume");
        setForm({
          ...emptyResume,
          ...r.data,
          experience: Array.isArray(r.data.experience) ? r.data.experience : [],
          education: Array.isArray(r.data.education) ? r.data.education : [],
        });
      } catch (error) {
        logWarn("Failed to load resume:", error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateItem = (key, idx, field) => (e) => {
    const arr = [...form[key]];
    arr[idx] = { ...arr[idx], [field]: e.target.value };
    setForm({ ...form, [key]: arr });
  };
  const addItem = (key, blank) => () => setForm({ ...form, [key]: [...form[key], withUid(blank)] });
  const removeItem = (key, idx) => () => {
    const arr = [...form[key]];
    arr.splice(idx, 1);
    setForm({ ...form, [key]: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        experience: form.experience.map(stripUid),
        education: form.education.map(stripUid),
      };
      await api.put("/resume", payload);
      toast.success("Resume saved");
    } catch (error) {
      logWarn("Resume save failed:", error);
      toast.error("Save failed");
    }
    finally { setSaving(false); }
  };

  const exportPdf = () => {
    const html = buildResumeHtml({ ...form, experience: form.experience.map(stripUid), education: form.education.map(stripUid) });
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("title", "resume-print");
    iframe.srcdoc = html;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        logError("Print failed:", err);
        toast.error("Could not open the print dialog");
      }
      setTimeout(() => iframe.remove(), 1000);
    };
    document.body.appendChild(iframe);
  };

  const runPolish = async () => {
    setPolishing(true);
    try {
      const r = await api.post("/ai/polish-resume");
      const d = r.data;
      const next = [];
      const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);
      for (const key of ["summary", "skills", "projects"]) {
        const orig = (form[key] || "").trim();
        const pol = (d[key] || "").trim();
        if (pol && pol !== orig) {
          next.push(makeDiff(key, titleCase(key), form[key] || "", d[key]));
        }
      }
      if (Array.isArray(d.experience)) {
        form.experience.forEach((e, i) => {
          const orig = (e.description || "").trim();
          const pol = (d.experience[i]?.description || "").trim();
          if (pol && pol !== orig) {
            const label = [e.title, e.company].filter(Boolean).join(" · ") || `Experience ${i + 1}`;
            next.push(makeDiff(`exp:${i}`, label, e.description || "", d.experience[i].description));
          }
        });
      }
      if (next.length === 0) {
        toast("AI had no wording changes to suggest.");
        setPolishOpen(false);
      } else {
        setDiffs(next);
      }
    } catch (err) {
      if (err.response?.status === 429) toast.warning(err.response.data.detail);
      else toast.error(err.response?.data?.detail || "Polish failed");
    } finally {
      setPolishing(false);
    }
  };

  const closePolish = () => { setPolishOpen(false); setDiffs([]); };
  const toggleDiff = (key) => setDiffs((ds) => ds.map((d) => (d.key === key ? { ...d, accepted: !d.accepted } : d)));
  const setAllDiffs = (val) => setDiffs((ds) => ds.map((d) => ({ ...d, accepted: val })));

  const applyPolish = () => {
    const accepted = diffs.filter((x) => x.accepted);
    setForm((f) => {
      const nf = { ...f };
      accepted.forEach((diff) => {
        if (diff.key.startsWith("exp:")) {
          const idx = Number(diff.key.split(":")[1]);
          nf.experience = nf.experience.map((e, i) => (i === idx ? { ...e, description: diff.polished } : e));
        } else {
          nf[diff.key] = diff.polished;
        }
      });
      return nf;
    });
    const n = accepted.length;
    closePolish();
    toast.success(n > 0 ? `Applied ${n} change${n > 1 ? "s" : ""} — review and Save` : "No changes applied");
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose your LinkedIn profile PDF");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/resume/import-linkedin", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setForm({
        ...emptyResume,
        ...d,
        experience: (Array.isArray(d.experience) ? d.experience : []).map(withUid),
        education: (Array.isArray(d.education) ? d.education : []).map(withUid),
      });
      setImportOpen(false);
      toast.success("Imported from LinkedIn — review the fields and Save");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="resume-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tiny">Profile</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Master resume</h1>
          <p className="text-zinc-600 mt-2">Source of truth for AI tailoring & cover letters.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            onClick={() => setImportOpen(true)}
            data-testid="linkedin-import-btn"
            variant="outline"
            className="rounded-none border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white h-10 px-4"
          >
            <Linkedin size={14} className="mr-1.5" />
            Import from LinkedIn
          </Button>
          <Button type="button" onClick={() => setPolishOpen(true)} variant="outline" className="rounded-none border-zinc-900 h-10 px-4" data-testid="resume-polish-btn">
            <Sparkles size={14} className="mr-1.5" /> Polish with AI
          </Button>
          <Button type="button" onClick={exportPdf} variant="outline" className="rounded-none border-zinc-300 h-10 px-4" data-testid="resume-export-pdf-btn">
            <FileDown size={14} className="mr-1.5" /> Export PDF
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-10 px-5" data-testid="resume-save-btn">
            <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* AI polish dialog */}
      <Dialog open={polishOpen} onOpenChange={(o) => { if (!o && !polishing) closePolish(); }}>
        <DialogContent className={"rounded-none border-2 border-zinc-900 " + (diffs.length ? "max-w-3xl" : "")} data-testid="polish-dialog">
          {diffs.length === 0 ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-black tracking-tight">Polish with AI</DialogTitle>
                <DialogDescription className="text-zinc-700 leading-relaxed">
                  AI will rewrite your summary, skills, project text, and every experience bullet into stronger,
                  metric-driven wording. Your employers, titles, dates, and schools stay exactly the same.
                </DialogDescription>
              </DialogHeader>
              <div className="border-2 border-amber-500 bg-amber-50 p-4 flex items-start gap-3" data-testid="polish-overwrite-warning">
                <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  You'll get to <span className="font-semibold">review each change and accept or skip it</span> before anything is applied. Nothing is saved until you Save.
                </p>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={closePolish} disabled={polishing} data-testid="polish-cancel-btn" className="rounded-none h-11 px-5 border-zinc-300">
                  Cancel
                </Button>
                <Button onClick={runPolish} disabled={polishing} data-testid="polish-run-btn" className="rounded-none h-11 px-5 bg-zinc-900 hover:bg-zinc-800 text-white">
                  {polishing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
                  {polishing ? "Polishing…" : "Polish now"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-black tracking-tight">Review AI suggestions</DialogTitle>
                <DialogDescription className="text-zinc-700">
                  {diffs.filter((d) => d.accepted).length} of {diffs.length} selected · accept the rewrites you like, skip the rest.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-3 -mt-1">
                <button onClick={() => setAllDiffs(true)} className="label-tiny hover:text-zinc-900" data-testid="diff-accept-all">Accept all</button>
                <span className="text-zinc-300">·</span>
                <button onClick={() => setAllDiffs(false)} className="label-tiny hover:text-zinc-900" data-testid="diff-skip-all">Skip all</button>
              </div>
              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1" data-testid="polish-review-list">
                {diffs.map((d) => (
                  <div key={d.key} className={"border-2 " + (d.accepted ? "border-zinc-900" : "border-zinc-200 opacity-70")} data-testid={`diff-${d.key}`}>
                    <div className="flex items-center justify-between gap-2 p-3 border-b border-zinc-200 bg-zinc-50">
                      <span className="font-semibold text-sm truncate">{d.label}</span>
                      <button
                        onClick={() => toggleDiff(d.key)}
                        className={"inline-flex items-center gap-1 px-3 py-1 text-xs uppercase tracking-wider font-semibold rounded-none border shrink-0 " + (d.accepted ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-500 border-zinc-300")}
                        data-testid={`diff-toggle-${d.key}`}
                      >
                        {d.accepted ? <><Check size={13} /> Accept</> : <><X size={13} /> Skip</>}
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200">
                      <div className="p-3">
                        <div className="label-tiny mb-1 text-zinc-400">Before</div>
                        <p className="text-sm text-zinc-500 whitespace-pre-wrap" data-testid={`diff-before-${d.key}`}>
                          {d.original ? d.beforeTokens.map((tok, ti) => (
                            <span key={ti} className={tok.c ? "bg-red-100 text-red-700 line-through decoration-red-400" : ""}>{tok.t}</span>
                          )) : "—"}
                        </p>
                      </div>
                      <div className={"p-3 " + (d.accepted ? "bg-emerald-50" : "")}>
                        <div className="label-tiny mb-1 text-emerald-700">After</div>
                        <p className="text-sm text-zinc-900 whitespace-pre-wrap" data-testid={`diff-after-${d.key}`}>
                          {d.afterTokens.map((tok, ti) => (
                            <span key={ti} className={tok.c ? "bg-emerald-200/70 text-emerald-900 font-medium" : ""}>{tok.t}</span>
                          ))}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={closePolish} data-testid="polish-review-cancel-btn" className="rounded-none h-11 px-5 border-zinc-300">
                  Cancel
                </Button>
                <Button onClick={applyPolish} data-testid="polish-apply-btn" className="rounded-none h-11 px-5 bg-zinc-900 hover:bg-zinc-800 text-white">
                  <Check size={14} className="mr-1.5" /> Apply {diffs.filter((d) => d.accepted).length} change{diffs.filter((d) => d.accepted).length === 1 ? "" : "s"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* LinkedIn import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => !importing && setImportOpen(o)}>
        <DialogContent className="rounded-none border-2 border-zinc-900" data-testid="linkedin-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">Import from LinkedIn</DialogTitle>
            <DialogDescription className="text-zinc-700 leading-relaxed">
              On LinkedIn, open your profile → <span className="font-semibold">More → Save to PDF</span>, then upload that file here. We'll read it with AI and fill in your resume.
            </DialogDescription>
          </DialogHeader>
          <div className="border-2 border-amber-500 bg-amber-50 p-4 flex items-start gap-3" data-testid="import-overwrite-warning">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              This will <span className="font-semibold">overwrite your existing resume fields</span> with the imported data. Save afterwards only if the result looks right.
            </p>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={onImportFile} className="hidden" data-testid="linkedin-file-input" />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing} data-testid="import-cancel-btn" className="rounded-none h-11 px-5 border-zinc-300">
              Cancel
            </Button>
            <Button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-choose-file-btn" className="rounded-none h-11 px-5 bg-[#0A66C2] hover:bg-[#08538f] text-white">
              {importing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
              {importing ? "Reading PDF…" : "Choose PDF & import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BASICS */}
      <Section label="Basics">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Full name"><Input data-testid="r-name" value={form.full_name} onChange={set("full_name")} className="rounded-none h-11" /></Field>
          <Field label="Headline"><Input data-testid="r-headline" value={form.headline} onChange={set("headline")} placeholder="Senior Backend Engineer · Python/Go" className="rounded-none h-11" /></Field>
          <Field label="Email"><Input data-testid="r-email" value={form.email} onChange={set("email")} className="rounded-none h-11" /></Field>
          <Field label="Phone"><Input data-testid="r-phone" value={form.phone} onChange={set("phone")} className="rounded-none h-11" /></Field>
          <Field label="City / location"><Input data-testid="r-location" value={form.location} onChange={set("location")} placeholder="New York" className="rounded-none h-11" /></Field>
          <Field label="Country">
            <Select value={form.country || "none"} onValueChange={(v) => setForm({ ...form, country: v === "none" ? "" : v, state: "" })}>
              <SelectTrigger data-testid="r-country" className="rounded-none h-11"><SelectValue placeholder="Select country" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none" data-testid="country-none">Not set</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c.isoCode} value={c.name} data-testid={`country-${c.isoCode}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="State / province">
            {states.length > 0 ? (
              <Select value={form.state || "none"} onValueChange={(v) => setForm({ ...form, state: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="r-state" className="rounded-none h-11"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none" data-testid="state-none">Not set</SelectItem>
                  {states.map((st) => (
                    <SelectItem key={st.isoCode} value={st.name} data-testid={`state-${st.isoCode}`}>{st.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input data-testid="r-state-text" value={form.state} onChange={set("state")} placeholder="State / province" className="rounded-none h-11" />
            )}
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Website / portfolio"><Input data-testid="r-website" value={form.website} onChange={set("website")} placeholder="https://yoursite.dev" className="rounded-none h-11" /></Field>
          <Field label="LinkedIn URL"><Input data-testid="r-linkedin" value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/you" className="rounded-none h-11" /></Field>
          <Field label="GitHub URL"><Input data-testid="r-github" value={form.github} onChange={set("github")} placeholder="https://github.com/you" className="rounded-none h-11" /></Field>
          <Field label="Other portfolio link"><Input data-testid="r-portfolio" value={form.portfolio} onChange={set("portfolio")} placeholder="Dribbble, Behance, etc." className="rounded-none h-11" /></Field>
        </div>
        <Field label="Summary">
          <Textarea data-testid="r-summary" value={form.summary} onChange={set("summary")} rows={4} className="rounded-none" placeholder="2-3 sentences about your engineering experience…" />
        </Field>
        <Field label="Skills">
          <Textarea data-testid="r-skills" value={form.skills} onChange={set("skills")} rows={3} className="rounded-none" placeholder="Python, Go, AWS, Postgres, Kubernetes…" />
        </Field>
      </Section>

      {/* EXPERIENCE */}
      <Section
        label="Experience"
        action={
          <Button type="button" variant="outline" onClick={addItem("experience", emptyExp)} className="rounded-none border-zinc-300 h-9" data-testid="add-experience-btn">
            <Plus size={14} className="mr-1.5" /> Add role
          </Button>
        }
      >
        {form.experience.length === 0 && (
          <div className="brutal-card p-6 text-center">
            <div className="label-tiny">No experience added</div>
            <p className="text-sm text-zinc-600 mt-2">Add roles one by one — start with the most recent.</p>
          </div>
        )}
        <div className="space-y-4">
          {form.experience.map((e, i) => (
            <div key={e._uid || i} className="brutal-card p-5" data-testid={`exp-card-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="label-tiny">Role #{i + 1}</span>
                <button type="button" onClick={removeItem("experience", i)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-exp-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Company"><Input data-testid={`exp-company-${i}`} value={e.company} onChange={updateItem("experience", i, "company")} className="rounded-none h-11" /></Field>
                <Field label="Title"><Input data-testid={`exp-title-${i}`} value={e.title} onChange={updateItem("experience", i, "title")} className="rounded-none h-11" /></Field>
                <Field label="Location"><Input data-testid={`exp-location-${i}`} value={e.location} onChange={updateItem("experience", i, "location")} placeholder="Remote · NYC" className="rounded-none h-11" /></Field>
                <div />
                <Field label="Start date"><Input data-testid={`exp-start-${i}`} value={e.start_date} onChange={updateItem("experience", i, "start_date")} placeholder="Mar 2022" className="rounded-none h-11" /></Field>
                <Field label="End date"><Input data-testid={`exp-end-${i}`} value={e.end_date} onChange={updateItem("experience", i, "end_date")} placeholder="Present" className="rounded-none h-11" /></Field>
              </div>
              <div className="mt-5">
                <Field label="What you did">
                  <Textarea data-testid={`exp-desc-${i}`} value={e.description} onChange={updateItem("experience", i, "description")} rows={4} className="rounded-none" placeholder="• Bullet point one&#10;• Bullet point two" />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* EDUCATION */}
      <Section
        label="Education"
        action={
          <Button type="button" variant="outline" onClick={addItem("education", emptyEdu)} className="rounded-none border-zinc-300 h-9" data-testid="add-education-btn">
            <Plus size={14} className="mr-1.5" /> Add school
          </Button>
        }
      >
        {form.education.length === 0 && (
          <div className="brutal-card p-6 text-center">
            <div className="label-tiny">No education added</div>
            <p className="text-sm text-zinc-600 mt-2">Add a school, bootcamp, or certification.</p>
          </div>
        )}
        <div className="space-y-4">
          {form.education.map((ed, i) => (
            <div key={ed._uid || i} className="brutal-card p-5" data-testid={`edu-card-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="label-tiny">School #{i + 1}</span>
                <button type="button" onClick={removeItem("education", i)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-edu-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="School"><Input data-testid={`edu-school-${i}`} value={ed.school} onChange={updateItem("education", i, "school")} className="rounded-none h-11" /></Field>
                <Field label="Degree / program"><Input data-testid={`edu-degree-${i}`} value={ed.degree} onChange={updateItem("education", i, "degree")} placeholder="B.S. Computer Science" className="rounded-none h-11" /></Field>
                <Field label="Location"><Input data-testid={`edu-location-${i}`} value={ed.location} onChange={updateItem("education", i, "location")} className="rounded-none h-11" /></Field>
                <div />
                <Field label="Start date"><Input data-testid={`edu-start-${i}`} value={ed.start_date} onChange={updateItem("education", i, "start_date")} placeholder="Sep 2016" className="rounded-none h-11" /></Field>
                <Field label="End date"><Input data-testid={`edu-end-${i}`} value={ed.end_date} onChange={updateItem("education", i, "end_date")} placeholder="May 2020" className="rounded-none h-11" /></Field>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* PROJECTS */}
      <Section label="Projects">
        <Field label="Projects">
          <Textarea data-testid="r-projects" value={form.projects} onChange={set("projects")} rows={5} className="rounded-none" placeholder="Project — short description and outcome" />
        </Field>
      </Section>

      <div className="flex justify-end pt-4 pb-4">
        <Button onClick={save} disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-11 px-6" data-testid="resume-save-btn-2">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save resume"}
        </Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="label-tiny block mb-2">{label}</Label>
    {children}
  </div>
);

const Section = ({ label, action, children }) => (
  <section className="mt-10">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">{label}</h2>
      {action}
    </div>
    <div className="space-y-5">{children}</div>
  </section>
);
